import type { CmmdWorkIntake } from "@cmmd-forge/contracts";

type PrimaryCmmdWorkIntake = CmmdWorkIntake;

export interface HomebaseCmmdIdentityContext {
  readonly displayName: string | null;
  readonly verified: boolean;
}

export interface HomebaseCmmdBrandContext {
  readonly name: string | null;
  readonly primaryColor: string | null;
  readonly logoUrl: string | null;
  readonly guidelines: ReadonlyArray<string>;
}

export interface HomebaseCmmdTaskBriefContext {
  readonly summary: string | null;
  readonly specDocumentId: string | null;
  readonly tags: ReadonlyArray<string>;
}

export interface HomebaseCmmdCampaignContext {
  readonly id: string;
  readonly title: string;
  readonly status: string | null;
}

export interface HomebaseCmmdProjectContext {
  readonly id: string;
  readonly title: string;
  readonly status: string | null;
  readonly campaignId: string | null;
  readonly workspaceId: string | null;
  readonly workspaceName: string | null;
  readonly workspaceIconUrl: string | null;
  readonly icon: string | null;
  readonly iconUrl: string | null;
  readonly teamId: string | null;
  readonly teamName: string | null;
}

export interface HomebaseCmmdTaskContext {
  readonly id: string;
  readonly title: string;
  readonly status: string | null;
  readonly priority: string | null;
  readonly projectId: string | null;
  readonly workspaceId: string | null;
  readonly workspaceName: string | null;
  readonly workspaceIconUrl: string | null;
  readonly teamId: string | null;
  readonly teamName: string | null;
  readonly dueDate: string | null;
  readonly updatedAt: string | null;
  readonly brief: HomebaseCmmdTaskBriefContext | null;
  /** Task ids this task is blocked by; empty array when none or not yet emitted by backend. */
  readonly dependsOn: ReadonlyArray<string>;
}

// This interface is intentionally richer than today's CMMD work-intake payload.
// It defines the stable Homebase context contract so future CMMD context fields
// can be adopted without changing Homebase component boundaries.
export interface HomebaseCmmdContext {
  readonly connected: boolean;
  readonly source: string;
  readonly fetchedAt: string;
  readonly warnings: ReadonlyArray<string>;
  readonly identity: HomebaseCmmdIdentityContext | null;
  readonly brand: HomebaseCmmdBrandContext | null;
  readonly campaigns: ReadonlyArray<HomebaseCmmdCampaignContext>;
  readonly projects: ReadonlyArray<HomebaseCmmdProjectContext>;
  readonly tasks: ReadonlyArray<HomebaseCmmdTaskContext>;
}

export function mapPrimaryCmmdWorkIntakeToHomebaseContext(
  intake: PrimaryCmmdWorkIntake,
): HomebaseCmmdContext {
  const identity: HomebaseCmmdIdentityContext | null = intake.identity
    ? {
        displayName:
          [intake.identity.firstName, intake.identity.lastName]
            .filter((p): p is string => typeof p === "string" && p.trim().length > 0)
            .join(" ")
            .trim() ||
          intake.identity.username ||
          intake.identity.email ||
          null,
        verified: true,
      }
    : null;

  const brand: HomebaseCmmdBrandContext | null = intake.workspace
    ? {
        name: intake.workspace.name,
        primaryColor: intake.workspace.color,
        logoUrl: intake.workspace.logoUrl ?? intake.workspace.iconUrl,
        guidelines: [],
      }
    : null;
  const workspaceIconUrlById = new Map<string, string | null>(
    intake.workspaces.map((workspace) => [
      workspace.id,
      workspace.iconUrl ?? workspace.logoUrl ?? null,
    ]),
  );
  const workspaceIconUrlByName = new Map<string, string | null>(
    intake.workspaces.map((workspace) => [
      workspace.name.trim().toLowerCase(),
      workspace.iconUrl ?? workspace.logoUrl ?? null,
    ]),
  );
  if (intake.workspace) {
    const activeIconUrl = intake.workspace.iconUrl ?? intake.workspace.logoUrl ?? null;
    workspaceIconUrlById.set(intake.workspace.id, activeIconUrl);
    workspaceIconUrlByName.set(intake.workspace.name.trim().toLowerCase(), activeIconUrl);
  }
  const resolveWorkspaceIconUrl = (
    workspaceId: string | null | undefined,
    workspaceName: string | null | undefined,
  ) => {
    if (workspaceId) {
      const byId = workspaceIconUrlById.get(workspaceId);
      if (byId) return byId;
    }
    if (workspaceName) {
      return workspaceIconUrlByName.get(workspaceName.trim().toLowerCase()) ?? null;
    }
    return null;
  };

  return {
    connected: intake.connected,
    source: intake.source,
    fetchedAt: intake.fetchedAt,
    warnings: intake.warnings,
    identity,
    brand,
    campaigns: intake.campaigns.map((campaign) => ({
      id: campaign.id,
      title: campaign.title,
      status: campaign.status,
    })),
    projects: intake.projects.map((project) => ({
      id: project.id,
      title: project.title,
      status: project.status,
      campaignId: project.campaignId,
      workspaceId: project.workspaceId ?? null,
      workspaceName: project.workspaceName ?? null,
      workspaceIconUrl: resolveWorkspaceIconUrl(project.workspaceId, project.workspaceName),
      icon: project.icon ?? null,
      iconUrl: project.iconUrl ?? null,
      teamId: project.teamId ?? null,
      teamName: project.teamName ?? null,
    })),
    tasks: intake.tasks.map((task) => ({
      id: task.id,
      title: task.title,
      status: task.status,
      priority: task.priority,
      projectId: task.projectId,
      workspaceId: task.workspaceId ?? null,
      workspaceName: task.workspaceName ?? null,
      workspaceIconUrl: resolveWorkspaceIconUrl(task.workspaceId, task.workspaceName),
      teamId: task.teamId ?? null,
      teamName: task.teamName ?? null,
      dueDate: task.dueDate,
      updatedAt: task.updatedAt,
      brief: null,
      dependsOn: task.dependsOn,
    })),
  };
}
